/**
 * Renders a college-specification American football field
 * with accurate dimensions and markings.
 *
 * Field specs:
 * - 160 feet wide
 * - Hash marks 60 feet from each edge (40 feet between hashes)
 * - 3 feet between hash marks (1 yard)
 * - Bottom-anchored rendering with y=0 at bottom
 * - Line of scrimmage at y=30 feet (darker line)
 * - Responsive scale based on available width
 *
 * Coordinate System:
 * - Storage: Feet with origin at bottom-left (0,0)
 * - Rendering: Web pixels with origin at top-left (0,0)
 * - Conversion handles the Y-axis flip
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { FIELD_WIDTH_FEET } from '@/constants'
import { useDimensions } from '@/hooks'
import type { Coordinate } from '@/types'
import { FieldMarkers } from './FieldMarkers'
import './football-field.css'

interface FootballFieldProps {
	className?: string
	onDimensionsChange?: (width: number, height: number) => void
	children?: React.ReactNode
	width?: number   // Optional override for consistent dimensions
	height?: number  // Optional override for consistent dimensions
}

export const FootballField = React.memo(function FootballField({
																   className,
																   onDimensionsChange,
																   children,
																   width: propWidth,
																   height: propHeight,
															   }: FootballFieldProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const measuredDimensions = useDimensions(containerRef)
	const dimensions = {
		width: propWidth ?? measuredDimensions.width,
		height: propHeight ?? measuredDimensions.height,
	}

	// Notify parent of dimension changes
	useEffect(() => {
		if (onDimensionsChange && dimensions.width > 0 && dimensions.height > 0) {
			onDimensionsChange(dimensions.width, dimensions.height)
		}
	}, [onDimensionsChange, dimensions.width, dimensions.height])

	// Create coordinate transformation function for FieldMarkers
	const transformPoint = useCallback((point: Coordinate): Coordinate => {
		if (!dimensions.width || !dimensions.height) return { x: 0, y: 0 }

		const scale = dimensions.width / FIELD_WIDTH_FEET
		return {
			x: point.x * scale,
			y: dimensions.height - (point.y * scale), // Y-axis flip
		}
	}, [dimensions.width, dimensions.height])

	return (
		<div
			ref={containerRef}
			className={`football-field ${className || ''}`}
		>
			{dimensions.width > 0 && dimensions.height > 0 && (
				<svg
					width={dimensions.width}
					height={dimensions.height}
					className="football-field-svg"
				>
					<FieldMarkers
						width={dimensions.width}
						height={dimensions.height}
						transformPoint={transformPoint}
					/>
				</svg>
			)}
			{children}
		</div>
	)
}, (prev, next) => {
	return prev.className === next.className &&
		prev.onDimensionsChange === next.onDimensionsChange &&
		prev.children === next.children &&
		prev.width === next.width &&
		prev.height === next.height
})